import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Grid, List, message, Row, Select, Space, Table, Input } from 'antd';
import { ActivitatorClient } from "../generated/activity_grpc_web_pb";
import GRPC_BACKEND_URL, {GRPC_CLIENT_OPTIONS} from "../grpcConfig";
import {Timestamp} from "google-protobuf/google/protobuf/timestamp_pb";
import dayjs from "dayjs";
import {Activity, ActivityTemplate} from "../generated/activity_pb";
import {insertUpdateAction} from "../App";
import AddActivityModal from "../Components/AddActivityModal";
import {tryParseUserDataFromLocalStorage} from "../utils/userStorage";
import {NO_SPORTTYPES, useSporttypes} from "../offline";
import {EmptyState, ErrorState, LoadingState, PageHeader, StatTile} from '@dowty/ui';
import {isGerman} from '../utils/language';
import {paginationItemRender} from '../utils/pagination';

const showTemplateRequestError = () => message.error('Unable to complete the template request');

function requestTemplateActivity(
    request: (callback: Parameters<ActivitatorClient['insertActivityTemplate']>[2]) => unknown,
    activity: Activity,
): Promise<boolean> {
    return new Promise(resolve => request((err, response) => {
        if (err || !response || response.getActivitiesList().length === 0) {
            showTemplateRequestError();
            resolve(false);
            return;
        }
        activity.setId(response.getActivitiesList()[0].getId());
        resolve(true);
    }));
}

const getEmptyTemplateTitle = (selectedTemplate: string) => selectedTemplate === 'All' ? 'No templates yet' : 'No activities in this template';

type activityTemplate = {
    templateName: string,
    activities: Activity[]
}

type templateRow = {
    id: number;
    templateName: string;
    sport?: string;
    parameterOne: number;
    parameterTwo: number;
    parameterOneUnit: string;
    parameterTwoUnit: string;
    templateIndex: number;
    templateActivityCount: number;
};

type column = {
    title: string,
    dataIndex: string,
    key?: string,
    render?: (_value: number | string, record: templateRow) => React.ReactNode,
    onCell?: (record: templateRow) => {rowSpan: number},
}

const defaultColumns: column[] = [
    { title: 'Template Name', dataIndex: 'templateName', key: 'templateName', onCell: row => ({rowSpan: row.templateIndex === 0 ? row.templateActivityCount : 0}) },
    { title: 'Sport', dataIndex: 'sport', key: 'sport' },
    { title: 'Amount', dataIndex: 'parameterOne', key: 'parameterOne', render: (value, row) => `${value} ${row.parameterOneUnit}`.trim() },
    { title: 'Weight', dataIndex: 'parameterTwo', key: 'parameterTwo', render: (value, row) => `${value} ${row.parameterTwoUnit}`.trim() },
];


const TemplateOverviewPage: React.FC = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const [selectedTemplate, setSelectedTemplate] = useState<string>('All');
    const [shownActivities, setShownActivities] = useState<activityTemplate[]>([]);
    const [selectableTemplates, setSelectableTemplates] = useState<string[]>(['All']);
    const [columns, setColumns] = useState<column[]>(defaultColumns);

    const [allTemplates, setAllTemplates] = useState<activityTemplate[]>([]);

    const activityClient = useMemo(() => new ActivitatorClient(GRPC_BACKEND_URL, null, GRPC_CLIENT_OPTIONS), []);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editData, setEditData] = useState<insertUpdateAction | null>(null);

    const sportTypes = useSporttypes().data ?? NO_SPORTTYPES;

    // State for adding a new template
    const [isAddingTemplate, setIsAddingTemplate] = useState<boolean>(false);
    const [newTemplateName, setNewTemplateName] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);

    const loadTemplates = useCallback(() => {
        const user = tryParseUserDataFromLocalStorage();
        if (!user) { setIsLoading(false); return; }
        setIsLoading(true);
        setLoadError(false);
        activityClient.getActivityTemplates(user, undefined, (err, res) => {
            setIsLoading(false);
            if (err || !res) { setLoadError(true); return; }
            const selectableTemplatesLocal: string[] = ["All"]
            const loadedTemplates: activityTemplate[] = []
            for (const activityTemplate of res.getActivitietemplatesList()) {
                selectableTemplatesLocal.push(activityTemplate.getTemplatename())
                loadedTemplates.push({templateName: activityTemplate.getTemplatename(), activities: activityTemplate.getActivitiesList()})
            }
            setAllTemplates(loadedTemplates)
            setShownActivities(loadedTemplates)
            setSelectableTemplates(selectableTemplatesLocal)
        })

    }, [activityClient]);

    useEffect(() => { loadTemplates(); }, [loadTemplates]);



    useEffect(() =>{
        if(selectedTemplate === "All"){
            setColumns(defaultColumns);
            setShownActivities(allTemplates)
        } else {
            setColumns([
                { title: 'Sport', dataIndex: 'sport'  },
                { title: 'Amount', dataIndex: 'parameterOne', render: (value, row) => `${value} ${row.parameterOneUnit}`.trim()},
                { title: 'Weight', dataIndex: 'parameterTwo', render: (value, row) => `${value} ${row.parameterTwoUnit}`.trim() },
            ])
            setShownActivities(allTemplates.filter(activity => activity.templateName === selectedTemplate))
        }
        // Activity handlers update the visible rows; this effect only responds to template selection.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTemplate]);

    async function handleDeleteFromModal(): Promise<boolean>{
        if (!editData || !(await deleteTemplateActivity(editData))) return false;
        setEditData(null)
        setEditMode(false)
        setIsModalOpen(false);
        return true;
    }
    async function deleteTemplateActivity(editData: insertUpdateAction): Promise<boolean> {
        const currentUser = tryParseUserDataFromLocalStorage();
        if (!currentUser) return false;

        const temp = new ActivityTemplate();
        temp.setTemplatename(editData.templateName ?? selectedTemplate);
        temp.setTemplateid(Number(editData.id));
        temp.setUser(currentUser);

        const activ = parseActivity(editData);
        temp.setActivitiesList([activ]);

        const success = await new Promise<boolean>((resolve) => {
            activityClient.deleteActivityTemplate(temp, undefined, (err, res) => {
                if (err) {
                    showTemplateRequestError();
                    resolve(false);
                    return;
                }
                if (!res || res.getActivitiesList().length === 0) { showTemplateRequestError(); resolve(false); return; }
                activ.setId(res.getActivitiesList()[0].getId());
                resolve(true);
            });
        });
        if (!success) return false;
        const remainingTemplates: activityTemplate[] = []
        const toShowAct:activityTemplate[] = []
        for(const act of allTemplates){
            const updatedTemplate: activityTemplate = {
                templateName: act.templateName,
                activities: act.activities.filter(activity => activity.getId() !== editData.id)
            }
            remainingTemplates.push(updatedTemplate)
            if(act.templateName === selectedTemplate || "All" === selectedTemplate ){
                toShowAct.push(updatedTemplate)
            }
        }

        setShownActivities(toShowAct)
        setAllTemplates(remainingTemplates)
        return true;
    }

    async function handleAddOrEditEverything(data: insertUpdateAction|activityTemplate): Promise<boolean>{
        if ('activities' in data) return false;
        return handleAddOrEdit(data);
    }



    async function handleAddOrEdit(data: insertUpdateAction): Promise<boolean>{
        const saved = await updateOrAddProcess(data);
        if (saved) setIsModalOpen(false);
        return saved;
    }

    async function updateOrAddProcess(data: insertUpdateAction): Promise<boolean>{
        if(editData == null || editData?.id === 0)
            return addProcess(data)
         else
            return editProcess(data)

    }

    async function addProcess(data:insertUpdateAction): Promise<boolean>{
        const temp = new ActivityTemplate()
        temp.setTemplatename(selectedTemplate)
        const currentUser = tryParseUserDataFromLocalStorage()
        if(currentUser != null)
            temp.setUser(currentUser)
        const activ = parseActivity(data)
        temp.setActivitiesList([activ])
        const success = await requestTemplateActivity(callback => activityClient.insertActivityTemplate(temp, undefined, callback), activ);
        if (!success) return false;
        const allActsVisible: activityTemplate[] = []
        const toShowAct:activityTemplate[] = []
        for(const act of allTemplates){
            if(act.templateName === selectedTemplate){
                const updatedTemplate: activityTemplate = {
                    templateName: selectedTemplate,
                    activities: [...act.activities, activ]
                }
                toShowAct.push(updatedTemplate)
                allActsVisible.push(updatedTemplate)
            } else {
                allActsVisible.push(act)
            }
        }

        if(!allActsVisible.some(template => template.templateName === selectedTemplate)){
            const updatedTemplate: activityTemplate = {
                templateName: selectedTemplate,
                activities: [activ]
            }
            toShowAct.push(updatedTemplate)
            allActsVisible.push(updatedTemplate)
        }

        setShownActivities(toShowAct)
        setAllTemplates(allActsVisible)
        return true;
    }

    function parseActivity(data: insertUpdateAction){
        const act = new Activity();

        act.setParameterone(data.parameterOne);
        act.setParametertwo(data.parameterTwo);
        const currentUser = tryParseUserDataFromLocalStorage()
        if(currentUser != null)
            act.setUser(currentUser)
        act.setSportype(sportTypes.getSportypeList().find(sport => sport.getSporttypename() === data.sport)!)
        if(data.date) {
            act.setTsinsert(Timestamp.fromDate(dayjs(data.date, "DD-MM-YYYY").hour(12).toDate()))
        }
        return act;
    }

    async function editProcess(data:insertUpdateAction): Promise<boolean>{
        const activityId = editData!.id;
        const temp = new ActivityTemplate()
        temp.setTemplatename(data.templateName ? data.templateName : selectedTemplate)
        temp.setTemplateid(Number(activityId))
        const currentUser = tryParseUserDataFromLocalStorage()
        if(currentUser != null)
            temp.setUser(currentUser)
        const activ = parseActivity(data)
        temp.setActivitiesList([activ])
        const success = await requestTemplateActivity(callback => activityClient.updateActivityTemplate(temp, undefined, callback), activ);
        if (!success) return false;
        const allActivities: activityTemplate[] = []
        const toShowAct:activityTemplate[] = []

        for (const template of allTemplates) {
            const updatedActivities = template.activities.map(activity => {
                if (activity.getId() === activityId) {
                    return activ; // Replace with updated activity
                }
                return activity;
            });

            const updatedTemplate = {
                templateName: template.templateName,
                activities: updatedActivities
            };

            allActivities.push(updatedTemplate);

            // Only add to shown activities if it matches the selected template or if "All" is selected
            if (template.templateName === selectedTemplate || selectedTemplate === "All") {
                toShowAct.push(updatedTemplate);
            }
        }

        setShownActivities(toShowAct)
        setAllTemplates(allActivities)
        setEditData(null)
        return true;
    }

    const handleAddTemplateClick = () => {
        if (newTemplateName.trim() !== '') {
            // Only update the template state here
            setSelectableTemplates(['All', ...selectableTemplates.filter(t => t !== 'All'), newTemplateName]);
            setSelectedTemplate(newTemplateName);
            setNewTemplateName('');
            setIsAddingTemplate(false);
        }
    };

    // Use the modal for adding new activity
    const handleAddActivity = () => {
        setEditData(null);
        setEditMode(false);
        setIsModalOpen(true);
    };

    function handleEdit(entry: templateRow){

        const myData:insertUpdateAction = {
            id: entry.id,
            sport: entry.sport ?? null,
            parameterOne: entry.parameterOne,
            parameterTwo: entry.parameterTwo,
            date: dayjs(new Date()).format('DD-MM-YYYY'),
            templateName: entry.templateName!,
        }
        setEditData(myData)
        setEditMode(true);
        setIsModalOpen(true);
    }

    function renderTemplateRows() {
        if (isLoading) return <LoadingState variant="list" label="Loading templates" />;
        if (loadError) return <ErrorState
            message={isGerman() ? 'Vorlagen konnten nicht geladen werden.' : 'Could not load templates.'}
            retry={loadTemplates}
            retryLabel={isGerman() ? 'Erneut versuchen' : 'Retry'}
        />;
        if (rows.length === 0) return <EmptyState title={getEmptyTemplateTitle(selectedTemplate)} />;
        if (isMobile) return <List className="template-activity-list" dataSource={rows} renderItem={row => <List.Item key={row.id}>
            <Button type="text" block onClick={() => handleEdit(row)} aria-label={`Edit ${row.sport} in ${row.templateName}`}>
                <Space wrap><span>{row.sport}</span><span>{row.parameterOne} {row.parameterOneUnit}</span><span>{row.parameterTwo} {row.parameterTwoUnit}</span>{row.templateIndex === 0 && <span>{row.templateName}</span>}</Space>
            </Button>
        </List.Item>} />;
        return <Table
            dataSource={rows}
            columns={[...columns, {title: isGerman() ? 'Aktion' : 'Action', dataIndex: 'id', key: 'action', render: (_value: number | string, row: templateRow) => <Button type="link" aria-label={`${isGerman() ? 'Bearbeiten' : 'Edit'} ${row.sport} in ${row.templateName}`} onClick={() => handleEdit(row)}>{isGerman() ? 'Bearbeiten' : 'Edit'}</Button>}]}
            pagination={{pageSize: 5, itemRender: paginationItemRender}}
            rowKey="id"
            size="middle"
        />;
    }

    function buildTemplateRows(templates: activityTemplate[]) {
        return templates.flatMap(template => template.activities.map((activity, templateIndex) => ({
            id: activity.getId(),
            templateName: template.templateName,
            sport: activity.getSportype()?.getSporttypename(),
            parameterOne: activity.getParameterone(),
            parameterTwo: activity.getParametertwo(),
            parameterOneUnit: activity.getSportype()?.getParameterone() ?? '',
            parameterTwoUnit: activity.getSportype()?.getParametertwo() ?? '',
            templateIndex,
            templateActivityCount: template.activities.length,
        })));
    }

    const dropdownRender = (menu: React.ReactNode) => {
        return (
            <div>
                {menu}
                {isAddingTemplate ? (
                    <div style={{ padding: '8px' }}>
                        <Input
                            placeholder="Enter new template"
                            value={newTemplateName}
                            onChange={(e) => setNewTemplateName(e.target.value)}
                            style={{ width: 200, marginBottom: '8px' }}
                        />
                        <Button
                            type="primary"
                            onClick={handleAddTemplateClick}
                            style={{ width: 200 }}
                        >
                            Add
                        </Button>
                    </div>
                ) : (
                    <Button
                        type="dashed"
                        onClick={() => setIsAddingTemplate(true)}
                        style={{ width: '100%', marginTop: '8px' }}
                    >
                        Add Template
                    </Button>
                )}
            </div>
        );
    };

    const rows = buildTemplateRows(shownActivities);
    return <div className="sf-templates-page">
        <PageHeader title="Templates" subtitle="Quickly repeat a saved activity routine" />
        <Row gutter={[{xs: 12, md: 16}, {xs: 12, md: 16}]}>
            <Col xs={12} sm={8}><StatTile label="Saved templates" value={allTemplates.length} /></Col>
            <Col xs={12} sm={8}><StatTile label="Saved activities" value={allTemplates.reduce((count, template) => count + template.activities.length, 0)} /></Col>
        </Row>
        <Card>
            <Row gutter={[12, 12]} justify="space-between" align="middle">
                <Col xs={24} sm={12}>
                    <Select aria-label="Filter templates" value={selectedTemplate} onChange={setSelectedTemplate} popupRender={dropdownRender} popupMatchSelectWidth={240}>
                        {selectableTemplates.map(template => <Select.Option key={template} value={template}>{template}</Select.Option>)}
                    </Select>
                </Col>
                {selectedTemplate !== 'All' && <Col><Button onClick={handleAddActivity} type="primary">Add Activity</Button></Col>}
            </Row>
            {renderTemplateRows()}
        </Card>
        <AddActivityModal
                isOpen={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                onOk={handleAddOrEditEverything}
                onDelete={handleDeleteFromModal}
                mode={editMode ? 'edit' : 'add'}
                initialData={editData}
                actualSportTypes={sportTypes}
                isTemplateCreation={true}
        />
    </div>;
};

export default TemplateOverviewPage;
